// Plan 30-03 Task 1 — LetterCubeSpriteMount @pixi/react adapter.
//
// Bridges React's declarative pixi-react surface with the imperative
// LetterCubeSprite class. One mount per board cell; LetterLayer maps the
// PlayerView.board[] through this component.
//
// Design:
//   - Use `<pixiContainer ref={...}>` and `addChild` the imperative sprite
//     into that container on first ref. @pixi/react owns the JSX node; we
//     own the imperative sprite *inside* it.
//   - First ref pass: construct the LetterCubeSprite + addChild + position.
//   - Subsequent renders: call sprite.updateCell(cell, theme) to diff/apply
//     server-authoritative changes; reposition only if (x, y) changed.
//   - Unmount: ref called with null → discard the local sprite ref. The
//     parent <pixiContainer> destroy cascade tears down our addChild'd
//     LetterCubeSprite via Pixi's Container.destroy({ children: true })
//     chain — which calls our override destroy() and drains tweens from the
//     shared group (Pitfall 5 lock established in Plan 30-02).
//
// Why ref callback (not useEffect + useImperativeHandle):
//   - Plan 30-RESEARCH §3.6 recommended this pattern: pixi-react gives us
//     the *Pixi.Container instance* directly via ref. There's no JSX-state
//     to reconcile in between — we own the inner sprite entirely. Using
//     useEffect would add an unnecessary render-to-effect lag.
//
// EXPOSURE for dev/test shims:
//   - Only the /dev/cell-swatch route's CellSwatchRoute.tsx populates the
//     window.__cellSprites Map for the reduced-motion spec. LetterLayer
//     in production never reads or writes that map; the LetterCubeSpriteMount
//     adapter merely exposes a small registry via devGetCellSprite()
//     (unused in production code paths). Tree-shaking of the
//     /dev/cell-swatch chunk in prod builds (see routes.tsx) ensures the
//     CellSwatchRoute is the only writer to window.__cellSprites and that
//     code never loads in production.

import type { Container as PixiContainer } from 'pixi.js'
import type { JSX } from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  LetterCubeSprite,
  type LetterCubeSpriteProps,
} from './LetterCubeSprite'

/**
 * Props for the LetterCubeSpriteMount adapter. Extends the imperative
 * sprite's constructor props with screen-space placement (x/y are absolute
 * within the parent pixiContainer, in logical pixels).
 *
 * Phase 31 (RUNE-32 / Plan 31-04): `affinityLetters` is threaded down from
 * MatchScreen → LetterLayer → here so the sprite-level `updateAffinity` flips
 * the Phase 30 `_affinityHalo` slot's visibility for caller-affinity-matched
 * cells. Defaults to `[]` to keep existing callers (e.g. /dev/cell-swatch
 * route, replay routes) working without a forced upgrade.
 */
export interface LetterCubeSpriteMountProps extends LetterCubeSpriteProps {
  x: number
  y: number
  affinityLetters?: ReadonlyArray<string>
  /** Phase 32 Plan 32-05 (RUNE-35) — caller's current_turn_idx. Threaded
   * down so the sprite-level $cascadeFrontier subscription can gate its
   * spawning-in trigger by the active cascade window (T6 / T11). */
  currentTurnIdx?: number
  /** Phase 2 (court-opens-at-the-center) — true when this cell is part of the
   * NEW center-inserted cohort for the current grown board (derived by
   * LetterLayer from `centerGrowWindow(board.length)`). Drives the sprite's
   * seam-burst spawn-in gate. */
  isNewThisGrow?: boolean
  /** Phase 2 — this cell's 0-based ordinal within the new center cohort
   * (cell_idx - window.start). Gates the cascade so the seam opens cell-by-cell
   * against the reveal FSM's frontier. */
  cohortOrdinal?: number
}

/**
 * Phase 4 — apply a position update to a mounted cube sprite. If the cube's
 * logical position changed under full motion (the field grew), HOLD the old
 * position and glide to the new cell on the $boardReanchor beat via
 * queueReanchor; otherwise snap (reduced motion, or first mount where the
 * position already matches). Exported so the diff useEffect and its unit
 * test exercise the SAME logic (no mirror drift).
 */
export function applyMountPosition(
  sprite: LetterCubeSprite,
  x: number,
  y: number,
  reducedMotion: boolean,
): void {
  const moved = sprite.x !== x || sprite.y !== y
  if (moved && !reducedMotion) {
    sprite.queueReanchor(x, y)
  } else {
    sprite.x = x
    sprite.y = y
  }
}

/**
 * Module-local cell-sprite registry. Unused in production code paths;
 * exposed for `devGetCellSprite()` consumers (currently no callers — the
 * /dev/cell-swatch route maintains its own window.__cellSprites map
 * because its sprites are constructed directly, not via this adapter).
 */
const CELL_SPRITES: Map<number, LetterCubeSprite> = new Map()

/** Return the live LetterCubeSprite for a given cell_idx (if mounted via
 * LetterCubeSpriteMount). */
export function devGetCellSprite(
  cellIdx: number,
): LetterCubeSprite | undefined {
  return CELL_SPRITES.get(cellIdx)
}

/**
 * @pixi/react adapter: mounts a LetterCubeSprite imperative subclass inside
 * a pixi-react `<pixiContainer>` and bridges React prop updates to the
 * sprite's updateCell() diff surface.
 */
export function LetterCubeSpriteMount(
  props: LetterCubeSpriteMountProps,
): JSX.Element {
  const spriteRef = useRef<LetterCubeSprite | null>(null)
  const cellIdxRef = useRef<number>(props.cell.cell_idx)

  // Latest props for the construct-once ref callback to read at attach time.
  // The ref callback must be STABLE (see below), so it can't close over the
  // per-render `props`; it reads them through this ref instead.
  const latestProps = useRef(props)
  latestProps.current = props

  // Phase 31 (RUNE-32) — memoize the affinity Set so the per-render
  // `sprite.updateAffinity(set)` call is cheap when affinityLetters
  // reference is stable across renders (LetterLayer hands down the same
  // `your_affinity_letters` tuple identity until a new PlayerView lands).
  const affinitySet = useMemo<ReadonlySet<string>>(
    () => new Set(props.affinityLetters ?? []),
    [props.affinityLetters],
  )

  // STABLE ref callback (empty deps) — construct the sprite exactly ONCE on
  // attach and tear it down on unmount. This MUST be stable: an inline ref
  // callback (new identity every render) is re-invoked by React with `null`
  // then the node on EVERY render, which previously nulled `spriteRef` and
  // then re-entered the construct branch — building a fresh LetterCubeSprite
  // and `addChild`-ing it on top of the old one each render. Because the
  // tintWash is drawn at alpha 0.25, those washes composited and saturated
  // the tile red/blue after ~10 re-renders (any word-phase re-render: tapping
  // the WORD pill OR tracing letters). A stable useCallback ref is invoked
  // only on real mount/unmount, so it can never stack sprites. Per-render
  // diffing happens in the useEffect below.
  const attachRef = useCallback((container: PixiContainer | null): void => {
    if (!container) {
      // Real unmount: drop our cached ref. The LetterCubeSprite is destroyed
      // via the parent container's destroy chain (Pixi cascades
      // destroy({children:true}) → our override destroy() drains tweens —
      // Pitfall 5 lock from Plan 30-02).
      if (cellIdxRef.current !== undefined) {
        CELL_SPRITES.delete(cellIdxRef.current)
      }
      spriteRef.current = null
      return
    }
    if (spriteRef.current) return // already constructed — never reconstruct
    const p = latestProps.current
    const sprite = new LetterCubeSprite({
      cell: p.cell,
      theme: p.theme,
      reducedMotion: p.reducedMotion,
      textures: p.textures,
      tweenGroup: p.tweenGroup,
      particles: p.particles,
    })
    sprite.x = p.x
    sprite.y = p.y
    container.addChild(sprite)
    spriteRef.current = sprite
    cellIdxRef.current = p.cell.cell_idx
    CELL_SPRITES.set(p.cell.cell_idx, sprite)
  }, [])

  // Per-render diff into the existing sprite (server-authoritative cell/theme
  // changes, repositioning, affinity halo, cascade turn-idx). All of these are
  // diff-gated / idempotent inside the sprite. Runs immediately after the ref
  // attaches the sprite on first mount, then only when one of the actual diff
  // inputs changes identity (PERF-08 — the previous empty-dep form re-ran all
  // five sprite-mutation calls on EVERY render × 60 cells; LetterLayer re-keys
  // the whole layer on theme/reducedMotion changes, so construction inputs
  // never change in place — only cell data / position / affinity / turn-idx).
  //
  // props.reducedMotion is intentionally absent from the deps array: it is
  // captured at LetterCubeSprite construction (the sprite's _reducedMotion
  // field is immutable) and LetterLayer re-keys the whole layer (destroying
  // and reconstructing all mounts) whenever $reducedMotion flips — so
  // props.reducedMotion cannot change on a live mount instance. Adding it to
  // deps would be correct-but-redundant; we suppress the lint warning here so
  // a future reader understands the deliberate omission.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reducedMotion is immutable per mount lifetime — see comment above
  useEffect(() => {
    const sprite = spriteRef.current
    if (sprite === null) return
    // PART-01 / Phase 4: when the field grows, an existing cube's logical
    // (x, y) changes. Under full motion, HOLD the old position and glide to the
    // new cell on the floor-drop beat ($boardReanchor) via queueReanchor —
    // do NOT teleport. Particle bursts in updateCell then emit at the cube's
    // current (old) on-board center, which is where it is visibly sitting at
    // the start of the glide. Reduced motion (and the first mount, where
    // sprite.x already equals props.x) snaps directly.
    applyMountPosition(sprite, props.x, props.y, props.reducedMotion)
    sprite.updateCell(props.cell, props.theme)
    sprite.updateAffinity(affinitySet)
    sprite.setCurrentTurnIdx(props.currentTurnIdx ?? 0)
    sprite.setIsNewThisGrow(props.isNewThisGrow ?? false)
    sprite.setCohortOrdinal(props.cohortOrdinal ?? 0)
  }, [
    props.cell,
    props.theme,
    props.x,
    props.y,
    affinitySet,
    props.currentTurnIdx,
    props.isNewThisGrow,
    props.cohortOrdinal,
  ])

  return <pixiContainer label={`cell-${props.cell.cell_idx}`} ref={attachRef} />
}
